import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpErrorResponse, HttpHandler, HttpEvent } from '@angular/common/http';

import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorModalService } from '@components/error-modal/error-modal.service';
import { recordUpdateHttpMethods, recordUpdatePutRules } from '@core/constants/record-update-put-patterns.constants';
import { environment } from '@environments/environment';
import { LoggerService } from '@core/services/logger.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private readonly errorService: ErrorModalService,
    private readonly logger: LoggerService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const properties = { correlationId: request.headers.get('X-Correlation-ID') ?? '' };

        if (error.error instanceof Error) {
          this.logger.error('An error occurred', error.error, properties);
        } else {
          this.logger.error(
            `HTTP ${error.status}: ${error.message}`,
            new Error(`HTTP ${error.status}: ${error.message}`),
            properties
          );
        }

        // Record-update PUT in admin/edit: let the component show one modal with API detail only.
        if (this.isRecordUpdatePut(request)) {
          const url = request.url.toLowerCase();
          if (url.includes('/programmes/')) {
            throw error;
          }
          throw error.error;
        }

        // PATCH associated-records 500: let the manage component show phase-specific message and optionally refresh.
        if (request.method === 'PATCH' && request.url.includes('associated-records') && error.status === 500) {
          throw error;
        }

        // PATCH qualification status: let the qualification component show a single context-aware modal.
        if (
          request.method === 'PATCH' &&
          request.url.toLowerCase().includes('/qualifications/') &&
          request.url.toLowerCase().includes('/status')
        ) {
          throw error;
        }

        const displayApiErrorDetail = environment.displayApiErrorDetail;
        let displayedMessage: string;

        if (error.status === 409) {
          displayedMessage =
            'The record you are trying to update has been modified by another user. Please refresh the page and try again.';
        } else {
          displayedMessage = displayApiErrorDetail
            ? this.getErrorMessageForDev(error)
            : this.getErrorMessageForTestUATProd(error);
        }

        this.errorService.open(displayedMessage).result.then(() => {
          if (error.status === 409) location.reload();
        });

        throw error.error;
      })
    );
  }

  /**
   * True for PUT requests that update a main record in admin/edit (micro-credential, programme, etc.).
   * We skip showing the interceptor modal so the manage component can show a single modal with only the API detail.
   */
  private isRecordUpdatePut(request: HttpRequest<unknown>): boolean {
    if (!recordUpdateHttpMethods.includes(request.method)) return false;
    const url = request.url.toLowerCase();
    return recordUpdatePutRules.some(rule => {
      if (!url.includes(rule.pathContains)) return false;
      if (rule.pathAlsoContains && !url.includes(rule.pathAlsoContains)) return false;
      if (rule.pathExcludes?.some(ex => url.includes(ex))) return false;
      return true;
    });
  }

  getErrorMessageForDev(error: HttpErrorResponse): string {
    return `${error.statusText} (${error.status}. ${error.message} ${error.error?.detail}`;
  }

  getErrorMessageForTestUATProd(error: HttpErrorResponse): string {
    if (error.status === 500) {
      return 'Your record failed to update/save due to a data conflict. Please refresh and retry your changes.';
    } else if (error.status > 0 && error.status < 500 && error.error?.detail) {
      /**
       * If the backend sent a meaningful error message, show it to the user
       */
      return error.error.detail;
    }

    return 'Something went wrong, please try again. If the issue continues, contact your system administrator.';
  }
}
